import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonIcon,
  IonButton,
  IonItem,
  IonLabel,
  IonList,
  IonChip,
  IonGrid,
  IonRow,
  IonCol,
  IonActionSheet,
  IonAlert,
  IonToast,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import {
  personOutline,
  mailOutline,
  callOutline,
  businessOutline,
  shieldCheckmarkOutline,
  statsChartOutline,
  timeOutline,
  trendingUpOutline,
  calendarOutline,
  cartOutline,
  cashOutline,
  ellipsisVertical,
  layersOutline,
} from 'ionicons/icons';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminApiService, UserWithStats, UserActivity } from '../services/adminApi';
import { getCurrencySymbol, DEFAULT_CURRENCY } from '../utils/currencyConstants';
import { format } from 'date-fns';
import CenteredLoader from '../components/CenteredLoader';
import { useAuth } from '../contexts/AuthContext';
import AdminUserInventoryTab from '../components/AdminUserInventoryTab';

interface RouteParams {
  userId: string;
}

const AdminUserDetails: React.FC = () => {
  const { t } = useTranslation();
  const { userId } = useParams<RouteParams>();
  const history = useHistory();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserWithStats | null>(null);
  const [userActivity, setUserActivity] = useState<UserActivity | null>(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showRoleAlert, setShowRoleAlert] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [newRole, setNewRole] = useState<'admin' | 'seller'>('seller');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeSegment, setActiveSegment] = useState<string>('overview');

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      history.replace('/dashboard');
      return;
    }
  }, [user, history]);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadUserData();
    }
  }, [user, userId]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [userDetails, activity] = await Promise.all([
        adminApiService.getUser(userId),
        adminApiService.getUserActivity(userId),
      ]);

      setUserData(userDetails);
      setUserActivity(activity);
    } catch (err: any) {
      console.error('Failed to load user data:', err);
      setError(err.message || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async () => {
    try {
      await adminApiService.updateUserRole(userId, newRole);
      setToastMessage(`User role updated to ${newRole}`);
      setShowToast(true);
      setShowRoleAlert(false);
      
      // Reload user data to reflect changes
      await loadUserData();
    } catch (err: any) {
      console.error('Failed to update user role:', err);
      setToastMessage(err.message || 'Failed to update role');
      setShowToast(true);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setIsDeleting(true);
      const result = await adminApiService.deleteUser(userId);
      
      setToastMessage(result.message || 'User and all associated data have been permanently deleted');
      setShowToast(true);
      setShowDeleteAlert(false);
      
      // Navigate back to admin dashboard after successful deletion
      setTimeout(() => {
        history.replace('/admin');
      }, 2000);
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      setToastMessage(err.message || 'Failed to delete user');
      setShowToast(true);
      setIsDeleting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `${getCurrencySymbol(DEFAULT_CURRENCY)}${amount.toFixed(2)}`;
  };

  const getActivityStatus = (isActive: boolean) => {
    return isActive ? (
      <IonChip color="success">
        <IonIcon icon={shieldCheckmarkOutline} />
        <IonLabel>Active</IonLabel>
      </IonChip>
    ) : (
      <IonChip color="medium">
        <IonIcon icon={timeOutline} />
        <IonLabel>Inactive</IonLabel>
      </IonChip>
    );
  };

  const getRoleChip = (role: string) => {
    return role === 'admin' ? (
      <IonChip color="danger">
        <IonIcon icon={shieldCheckmarkOutline} />
        <IonLabel>Admin</IonLabel>
      </IonChip>
    ) : (
      <IonChip color="primary">
        <IonIcon icon={personOutline} />
        <IonLabel>Seller</IonLabel>
      </IonChip>
    );
  };

  if (user?.role !== 'admin') {
    return (
      <IonPage>
        <IonContent>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <IonIcon icon={shieldCheckmarkOutline} className="text-6xl text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-red-600 mb-2">Access Denied</h2>
              <p className="text-gray-600 dark:text-gray-400">You don't have admin permissions.</p>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/admin" />
            </IonButtons>
            <IonTitle>User Details</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <CenteredLoader message="Loading user details..." />
        </IonContent>
      </IonPage>
    );
  }

  if (error || !userData) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/admin" />
            </IonButtons>
            <IonTitle>User Details</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <IonIcon icon={personOutline} className="text-6xl text-gray-400 mb-4" />
              <h2 className="text-xl font-semibold text-gray-600 mb-2">
                {error || 'User not found'}
              </h2>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/admin" />
          </IonButtons>
          <IonTitle>{userData.profile?.name || userData.email}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setShowActionSheet(true)}>
              <IonIcon icon={ellipsisVertical} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        <div className="px-4 py-4">
          {/* User Info Header Card */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <IonIcon icon={personOutline} className="mr-2" />
                  {userData.profile?.name || userData.email}
                </div>
                <div className="flex space-x-2">
                  {getRoleChip(userData.role)}
                  {getActivityStatus(userData.isActive)}
                </div>
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{userData.totalEvents}</p>
                  <p className="text-sm text-gray-500">Events</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{userData.totalSales}</p>
                  <p className="text-sm text-gray-500">Sales</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{userData.totalTickets}</p>
                  <p className="text-sm text-gray-500">Tickets</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-orange-600">
                    {formatCurrency(userData.revenueThisMonth)}
                  </p>
                  <p className="text-sm text-gray-500">This Month</p>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Segment Tabs */}
          <IonCard className="mt-4">
            <IonSegment 
              value={activeSegment} 
              onIonChange={(e) => setActiveSegment(e.detail.value as string)}
            >
              <IonSegmentButton value="overview">
                <IonIcon icon={statsChartOutline} />
                <IonLabel>Overview</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="inventory">
                <IonIcon icon={layersOutline} />
                <IonLabel>Inventory</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="details">
                <IonIcon icon={personOutline} />
                <IonLabel>Details</IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </IonCard>

          {/* Tab Content */}
          {activeSegment === 'overview' && (
            <>
              {/* Usage Statistics */}
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="flex items-center">
                    <IonIcon icon={statsChartOutline} className="mr-2" />
                    Usage Statistics
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonGrid>
                    <IonRow>
                      <IonCol size="6" sizeMd="3">
                        <div className="text-center p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                          <IonIcon icon={calendarOutline} className="text-3xl text-blue-600 mb-2" />
                          <p className="text-2xl font-bold text-blue-600">{userData.totalEvents}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Total Events</p>
                          <p className="text-xs text-green-600">+{userData.eventsThisMonth} this month</p>
                        </div>
                      </IonCol>

                      <IonCol size="6" sizeMd="3">
                        <div className="text-center p-4 bg-green-50 dark:bg-green-900 rounded-lg">
                          <IonIcon icon={cartOutline} className="text-3xl text-green-600 mb-2" />
                          <p className="text-2xl font-bold text-green-600">{userData.totalSales}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Total Sales</p>
                          <p className="text-xs text-green-600">+{userData.salesThisMonth} this month</p>
                        </div>
                      </IonCol>

                      <IonCol size="6" sizeMd="3">
                        <div className="text-center p-4 bg-purple-50 dark:bg-purple-900 rounded-lg">
                          <IonIcon icon={cashOutline} className="text-3xl text-purple-600 mb-2" />
                          <p className="text-xl font-bold text-purple-600">
                            {formatCurrency(userData.revenueThisMonth)}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">This Month</p>
                          <p className="text-xs text-gray-500">
                            {userData.revenueGrowth >= 0 ? '+' : ''}{userData.revenueGrowth.toFixed(1)}% vs last month
                          </p>
                        </div>
                      </IonCol>

                      <IonCol size="6" sizeMd="3">
                        <div className="text-center p-4 bg-orange-50 dark:bg-orange-900 rounded-lg">
                          <IonIcon icon={trendingUpOutline} className="text-3xl text-orange-600 mb-2" />
                          <p className="text-2xl font-bold text-orange-600">{userData.totalTickets}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Total Tickets</p>
                        </div>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonCardContent>
              </IonCard>

              {/* Recent Activity */}
              {userActivity && (
                <>
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="flex items-center">
                    <IonIcon icon={timeOutline} className="mr-2" />
                    Recent Sales
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList>
                    {userActivity.recentSales.slice(0, 5).map((sale) => (
                      <IonItem key={sale._id}>
                        <IonLabel>
                          <h2>{sale.eventId?.name || 'Unknown Event'}</h2>
                          <p>{sale.eventId?.venue} • {format(new Date(sale.saleDate), 'MMM dd, yyyy')}</p>
                          <p className="text-sm">Source: {sale.source || 'Manual'}</p>
                        </IonLabel>
                        <div slot="end" className="text-right">
                          <p className="font-semibold text-green-600">
                            {formatCurrency(sale.totalAmount)}
                          </p>
                          <IonChip color={sale.status === 'confirmed' ? 'success' : 'warning'} className="text-xs">
                            {sale.status}
                          </IonChip>
                        </div>
                      </IonItem>
                    ))}
                    {userActivity.recentSales.length === 0 && (
                      <IonItem>
                        <IonLabel>
                          <p className="text-gray-500">No recent sales</p>
                        </IonLabel>
                      </IonItem>
                    )}
                  </IonList>
                </IonCardContent>
              </IonCard>

              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="flex items-center">
                    <IonIcon icon={calendarOutline} className="mr-2" />
                    Recent Events
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList>
                    {userActivity.recentEvents.slice(0, 5).map((event) => (
                      <IonItem key={event._id}>
                        <IonLabel>
                          <h2>{event.name}</h2>
                          <p>{event.venue} • {format(new Date(event.date), 'MMM dd, yyyy')}</p>
                          <p className="text-sm">Created: {format(new Date(event.createdAt), 'MMM dd, yyyy')}</p>
                        </IonLabel>
                        <IonChip 
                          slot="end" 
                          color={event.status === 'active' ? 'success' : 'warning'}
                        >
                          {event.status}
                        </IonChip>
                      </IonItem>
                    ))}
                    {userActivity.recentEvents.length === 0 && (
                      <IonItem>
                        <IonLabel>
                          <p className="text-gray-500">No recent events</p>
                        </IonLabel>
                      </IonItem>
                    )}
                  </IonList>
                </IonCardContent>
              </IonCard>
                </>
              )}
            </>
          )}

          {/* Inventory Tab */}
          {activeSegment === 'inventory' && (
            <AdminUserInventoryTab userId={userId} />
          )}

          {/* Details Tab */}
          {activeSegment === 'details' && (
            <IonCard>
              <IonCardHeader>
                <IonCardTitle className="flex items-center">
                  <IonIcon icon={personOutline} className="mr-2" />
                  User Information
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList>
                  <IonItem>
                    <IonIcon icon={personOutline} slot="start" />
                    <IonLabel>
                      <h3>Name</h3>
                      <p>{userData.profile?.name || 'Not set'}</p>
                    </IonLabel>
                  </IonItem>

                  <IonItem>
                    <IonIcon icon={mailOutline} slot="start" />
                    <IonLabel>
                      <h3>Email</h3>
                      <p>{userData.email}</p>
                    </IonLabel>
                  </IonItem>

                  {userData.profile?.phone && (
                    <IonItem>
                      <IonIcon icon={callOutline} slot="start" />
                      <IonLabel>
                        <h3>Phone</h3>
                        <p>{userData.profile.phone}</p>
                      </IonLabel>
                    </IonItem>
                  )}

                  {userData.profile?.company && (
                    <IonItem>
                      <IonIcon icon={businessOutline} slot="start" />
                      <IonLabel>
                        <h3>Company</h3>
                        <p>{userData.profile.company}</p>
                      </IonLabel>
                    </IonItem>
                  )}

                  <IonItem>
                    <IonIcon icon={timeOutline} slot="start" />
                    <IonLabel>
                      <h3>Member Since</h3>
                      <p>{format(new Date(userData.createdAt), 'MMMM dd, yyyy')}</p>
                    </IonLabel>
                  </IonItem>

                  <IonItem>
                    <IonIcon icon={timeOutline} slot="start" />
                    <IonLabel>
                      <h3>Last Login</h3>
                      <p>{format(new Date(userData.lastLogin), 'MMMM dd, yyyy - HH:mm')}</p>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>
          )}
        </div>

        {/* Action Sheet */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          buttons={[
            {
              text: 'Change Role',
              handler: () => {
                setNewRole(userData.role === 'admin' ? 'seller' : 'admin');
                setShowRoleAlert(true);
              },
            },
            {
              text: 'Delete User',
              role: 'destructive',
              handler: () => {
                setShowDeleteAlert(true);
              },
            },
            {
              text: 'Cancel',
              role: 'cancel',
            },
          ]}
        />

        {/* Role Change Alert */}
        <IonAlert
          isOpen={showRoleAlert}
          onDidDismiss={() => setShowRoleAlert(false)}
          header="Change User Role"
          message={`Are you sure you want to change this user's role to ${newRole}?`}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Confirm',
              handler: handleRoleChange,
            },
          ]}
        />

        {/* Delete User Alert */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header="Delete User"
          message={`Are you sure you want to permanently delete ${userData?.profile?.name || userData?.email}? This action will delete all associated data including events, sales, tickets, suppliers, and fulfillments. This cannot be undone.`}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => setIsDeleting(false),
            },
            {
              text: isDeleting ? 'Deleting...' : 'Delete',
              role: 'destructive',
              handler: isDeleting ? undefined : handleDeleteUser,
            },
          ]}
        />

        {/* Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
        />
      </IonContent>
    </IonPage>
  );
};

export default AdminUserDetails;